<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useTranslation } from "i18next-vue";
import { useSaveSectionsAsTemplate } from "@/stores/queries/sections.query";
import { useGetReportSectionsTree } from "@/stores/queries";
import FieldPartTemplateTreePreview from "@/components/field-part-templates/FieldPartTemplateTreePreview.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import type { Section } from "@/types/sections.types";
import type { PartsTreeNode } from "@/types/field-observations.types";

const props = defineProps<{
  modelValue: boolean;
  reportId: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "saved"): void;
}>();

const { t } = useTranslation();

const reportIdRef = computed(() => props.reportId);
const { data: sectionsTree } = useGetReportSectionsTree(reportIdRef);

const name = ref("");
const description = ref("");

const { mutate: saveTemplate, isPending } = useSaveSectionsAsTemplate();

const show = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

// Build preview tree from sections
const previewNodes = computed((): PartsTreeNode[] => {
  if (!sectionsTree.value) return [];
  const buildNodes = (sections: Section[]): PartsTreeNode[] => {
    return sections.map((s) => ({
      name: s.title,
      children: s.children ? buildNodes(s.children) : [],
    }));
  };
  return buildNodes(sectionsTree.value);
});

// Reset form when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      name.value = "";
      description.value = "";
    }
  },
);

const close = () => {
  show.value = false;
};

const handleSave = () => {
  if (!name.value.trim() || previewNodes.value.length === 0) return;

  saveTemplate(
    {
      reportId: props.reportId,
      name: name.value.trim(),
      description: description.value.trim() || undefined,
    },
    {
      onSuccess: () => {
        emit("saved");
        close();
      },
    },
  );
};
</script>

<template>
  <el-dialog
    v-model="show"
    :title="t('report:photos.sections.templates.saveTitle')"
    width="480px"
    append-to-body
  >
    <el-form @submit.prevent="handleSave">
      <el-form-item>
        <el-input
          v-model="name"
          :placeholder="t('report:photos.sections.templates.namePlaceholder')"
          @keyup.enter="handleSave"
        />
      </el-form-item>

      <el-form-item>
        <el-input
          v-model="description"
          type="textarea"
          :rows="2"
          :placeholder="
            t('report:photos.sections.templates.descriptionPlaceholder')
          "
        />
      </el-form-item>
    </el-form>

    <!-- Preview -->
    <div v-if="previewNodes.length > 0" class="preview-section">
      <div class="preview-label">
        <i class="bi-eye me-1"></i>
        {{ t("report:photos.sections.templates.preview") }}
      </div>
      <div class="preview-tree">
        <FieldPartTemplateTreePreview :nodes="previewNodes" />
      </div>
    </div>

    <template #footer>
      <div class="d-flex gap-2 justify-content-end">
        <DRBtn :label="t('cancel')" variant="outline" @click="close" />
        <DRBtn
          :label="t('report:photos.sections.templates.save')"
          variant="primary"
          :disabled="!name.trim() || previewNodes.length === 0"
          :loading="isPending"
          @click="handleSave"
        />
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.preview-section {
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  overflow: hidden;
}

.preview-label {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: var(--bs-tertiary-bg);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.preview-tree {
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
}
</style>
